import React, { useEffect, useState } from 'react';
import { ReportHeader } from '../../header/report-header';
import { DataTable, DataTablePageEvent } from 'primereact/datatable';
import { Column } from 'primereact/column';
import 'primereact/resources/themes/lara-light-purple/theme.css';
import { LecturesSideBarFilter } from './side-bar-filter/lectures-side-bar-filter';
import Colors from '../../../../utils/colors';
import { LecturesSideBarFilterDatamodel } from './side-bar-filter/lectures-side-bar-filter-datamodel';
import { getLecturesReportData } from './operations/get-lectures-report-data';
import { ReportLecturesType } from '../../../../api/types/reports/education/report-lectures.type';
import { format } from 'date-fns';
import dateUtils from '../../../../utils/date-format';
import dateFormat from '../../../../utils/date-format';

export const ReportLectures = () => {
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<LecturesSideBarFilterDatamodel>({
    branchesIds: [],
    programIds: [],
    startYears: [],
    statuses: [],
    lectureDateStart: null,
    lectureDateEnd: null,
    exceptionThresholdMinMinutes: 3,
    exceptionThresholdMaxMinutes: null,
  });
  const [data, setData] = useState<ReportLecturesType[]>([]);
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  // Mock data for demonstration
  const lecturesData = [
    {
      branch: 'ירושלים',
      program: 'הנדסת תוכנה',
      programId: '2023',
      courseName: 'מבוא לתכנות',
      lectureDate: '2024-03-15',
      scheduledStartTime: '09:00',
      scheduledEndTime: '12:00',
      actualStartTime: '09:05',
      actualEndTime: '12:10',
      lecturer: 'ד"ר ישראל ישראלי',
      status: 'מפגש שלא נסגר',
    },
    {
      branch: 'תל אביב',
      program: 'מדעי המחשב',
      programId: '2023',
      courseName: 'אלגוריתמים',
      lectureDate: '2024-03-14',
      scheduledStartTime: '13:00',
      scheduledEndTime: '17:00',
      actualStartTime: '13:15',
      actualEndTime: '17:30',
      lecturer: 'פרופ׳ יעקב כהן',
      status: 'מפגש חורג בשעות',
    },
  ];

  const getTimeExceptionStyle = (minutes: number) => ({
    color: minutes > 0 ? Colors.dangerText : Colors.approveText2,
  });

  const calculateTimeDifference = (
    scheduled: string,
    actual: string,
  ): number => {
    const [scheduledHours, scheduledMinutes] = scheduled.split(':').map(Number);
    const [actualHours, actualMinutes] = actual.split(':').map(Number);
    return (
      actualHours * 60 +
      actualMinutes -
      (scheduledHours * 60 + scheduledMinutes)
    );
  };

  const getStatusSeverity = (status: string) => {
    const severityMap: { [key: string]: 'success' | 'warning' | 'danger' } = {
      'מפגש שלא נסגר': 'danger',
      'מפגש חורג בשעות': 'warning',
    };
    return severityMap[status] || 'info';
  };

  const getStatusStyle = (status: string) => {
    const isError = status === 'מפגש שלא נסגר';
    return {
      backgroundColor: isError ? '#FEE2E2' : '#FEF3C7',
      color: isError ? Colors.dangerText : Colors.warningYelloText,
      padding: '4px 8px',
      borderRadius: '4px',
      width: '120px',
      textAlign: 'center' as const,
      display: 'inline-block',
    };
  };

  const statusTemplate = (rowData: any) => (
    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
      <div style={getStatusStyle(rowData.status)}>{rowData.status}</div>
    </div>
  );

  const formatDateTime = (date: Date) => {
    return format(date, 'HH:mm');
  };

  /** Rounds to at most 2 decimal places for stable minute display. */
  const formatMinutesMagnitudeForDisplay = (minutes: number) => {
    const rounded = Math.round(Math.abs(minutes) * 100) / 100;
    if (Number.isInteger(rounded)) {
      return String(rounded);
    }
    return rounded.toFixed(2).replace(/\.?0+$/, '');
  };

  const formatMinutes = (minutes: number) => {
    const abs = Math.abs(minutes);
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    if (h === 0) return `${formatMinutesMagnitudeForDisplay(minutes)} דק׳`;
    return `${h}:${m.toString().padStart(2, '0')} ש׳`;
  };

  const ExceptionBadge = ({
    minutes,
    compact,
  }: {
    minutes: number;
    compact?: boolean;
  }) => {
    const isLate = minutes > 0;
    const bg = isLate ? '#FEE2E2' : '#DCFCE7';
    const fg = isLate ? Colors.dangerText : Colors.approveText2;
    const label = compact
      ? `${isLate ? '+' : '-'}${formatMinutesMagnitudeForDisplay(minutes)}`
      : `${isLate ? '+' : '-'}${formatMinutes(minutes)}`;

    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: compact ? '1px 8px' : '2px 8px',
          borderRadius: '999px',
          background: bg,
          color: fg,
          fontSize: compact ? '0.75rem' : '0.85rem',
          fontWeight: 600,
          lineHeight: compact ? '16px' : '18px',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
    );
  };

  const startTimeTemplate = (rowData: ReportLecturesType) => {
    const scheduled = rowData.scheduledStartTime;
    const actual = rowData.startAtActualTime;

    if (!scheduled) return <div style={{ textAlign: 'right' }}>-</div>;

    const difference = actual
      ? calculateTimeDifference(
          formatDateTime(scheduled),
          formatDateTime(actual),
        )
      : 0;

    return (
      <div style={{ textAlign: 'right', display: 'grid', gap: '2px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            flexDirection: 'row-reverse',
            gap: '8px',
          }}
        >
          <span style={{ fontWeight: 600 }}>{formatDateTime(scheduled)}</span>
          {difference !== 0 && actual && (
            <ExceptionBadge minutes={difference} compact />
          )}
        </div>
        {actual && (
          <div style={{ color: '#6B7280', fontSize: '0.85rem' }}>
            בפועל: {formatDateTime(actual)}
          </div>
        )}
      </div>
    );
  };

  const endTimeTemplate = (rowData: ReportLecturesType) => {
    const scheduled = rowData.scheduledEndTime;
    const actual = rowData.endAtActualTime;

    if (!scheduled) return <div style={{ textAlign: 'right' }}>-</div>;

    const difference = actual
      ? calculateTimeDifference(
          formatDateTime(scheduled),
          formatDateTime(actual),
        )
      : 0;

    return (
      <div style={{ textAlign: 'right', display: 'grid', gap: '2px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            flexDirection: 'row-reverse',
            gap: '8px',
          }}
        >
          <span style={{ fontWeight: 600 }}>{formatDateTime(scheduled)}</span>
          {difference !== 0 && actual && (
            <ExceptionBadge minutes={difference} />
          )}
        </div>
        {actual && (
          <div style={{ color: '#6B7280', fontSize: '0.85rem' }}>
            בפועל: {formatDateTime(actual)}
          </div>
        )}
      </div>
    );
  };

  const totalExceptionTemplate = (rowData: ReportLecturesType) => {
    const minutes = rowData.minutesExceeded;
    if (minutes === null || minutes === undefined) {
      return <div style={{ textAlign: 'right' }}>-</div>;
    }
    if (minutes === 0) {
      return (
        <div style={{ textAlign: 'right', color: Colors.approveText2 }}>
          0 דק׳
        </div>
      );
    }
    return (
      <div style={{ textAlign: 'right' }}>
        <ExceptionBadge minutes={minutes} />
      </div>
    );
  };

  const fetchReportData = async (
    nextFilters: LecturesSideBarFilterDatamodel,
    newOffset: number = offset,
    newLimit: number = limit,
  ) => {
    setFilters(nextFilters);
    setOffset(newOffset);
    setLimit(newLimit);
    await getLecturesReportData(
      nextFilters,
      newOffset,
      newLimit,
      setLoading,
      setData,
      setTotal,
    );
  };

  useEffect(() => {
    fetchReportData(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = async (
    searchFilters: LecturesSideBarFilterDatamodel,
  ) => {
    await fetchReportData(searchFilters, 0, limit);
  };

  const onPageChange = (event: DataTablePageEvent) => {
    fetchReportData(filters, event.first, event.rows);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <ReportHeader
        title="דו״ח מפגשים חורגים בהצלחה"
        description="דו״ח זה מציג מידע על מפגשים שלא נסגרו בהצלחה, כולל מפגשים שלא נסגרו כלל ומפגשים החורגים בשעות."
        metadata={[
          { label: 'מטרת הדו״ח', value: 'מעקב אחר סגירת מפגשים' },
          { label: 'תדירות עדכון', value: 'יומי' },
        ]}
        category="education"
      />

      <div style={{ display: 'flex', gap: '20px' }}>
        <div
          style={{
            height: 'calc(100vh - 230px)',
            minHeight: '100%',
            width: '300px',
            minWidth: '250px',
          }}
        >
          <LecturesSideBarFilter onSearch={handleSearch} />
        </div>

        {/* Table Section */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <div
            style={{ fontSize: '14px', color: '#4b5563', textAlign: 'right' }}
          >
            סה״כ רשומות: {total.toLocaleString()}
          </div>
          <DataTable
            value={data}
            rowHover
            loading={loading}
            paginator
            rows={limit}
            first={offset}
            totalRecords={total}
            onPage={onPageChange}
            lazy={true}
            rowClassName={() => 'cursor-pointer'}
            rowsPerPageOptions={[10, 20, 50, 100]}
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            emptyMessage={
              <div style={{ textAlign: 'right' }}>לא נמצאו נתונים</div>
            }
            dataKey="workingHoursId"
            scrollable
            scrollHeight="calc(100vh - 230px)"
            tableStyle={{
              direction: 'rtl',
            }}
          >
            <Column
              field="branchName"
              header="שלוחה"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="abstractProgramName"
              header="תוכנית לימודים"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="annualSerialProgramNumber"
              header="מזהה תוכנית"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="courseName"
              header="שם קורס"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="additionalCourseName"
              header="שם קורס נוסף"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData: ReportLecturesType) =>
                rowData.additionalCourseName || '-'
              }
            />
            <Column
              field="lectureDate"
              header="תאריך מפגש"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData: ReportLecturesType) =>
                rowData.scheduledStartTime
                  ? dateFormat.displayFormatDate(rowData.scheduledStartTime)
                  : '-'
              }
            />
            <Column
              field="scheduledStartTime"
              header="שעת התחלה"
              body={startTimeTemplate}
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="scheduledEndTime"
              header="שעת סיום"
              body={endTimeTemplate}
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="minutesExceeded"
              header="סה״כ חריגה"
              body={totalExceptionTemplate}
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
            <Column
              field="lecturer"
              header="מרצה"
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
              body={(rowData: ReportLecturesType) => (
                <div style={{ textAlign: 'right' }}>
                  {`${rowData.firstName ?? ''} ${rowData.lastName ?? ''}`.trim() ||
                    '-'}
                </div>
              )}
            />
            <Column
              field="status"
              header="סטטוס"
              body={statusTemplate}
              style={{ textAlign: 'right' }}
              headerStyle={{ textAlign: 'right' }}
            />
          </DataTable>
        </div>
      </div>
    </div>
  );
};
